/** Critérios de filtro da inbox de Conversas (eixo atenção + status). */

export type ConversationAttentionFilter = "mine" | "attention" | "ia" | "all";
export type ConversationStatusFilter = "open" | "closed";

export const ATTENTION_FILTER_LABELS: Record<
  ConversationAttentionFilter,
  { label: string; title: string; badgeClass: string }
> = {
  mine: {
    label: "Minhas",
    title: "Conversas atribuídas a você",
    badgeClass: "bg-primary/20 text-primary",
  },
  attention: {
    label: "Escalado / Urgente",
    title: "Aguardando atendimento humano",
    badgeClass: "bg-orange-500/20 text-orange-600",
  },
  ia: {
    label: "IA ativa",
    title: "Conversas abertas com a IA respondendo (state ativo)",
    badgeClass: "bg-green-500/20 text-green-600",
  },
  all: {
    label: "Todas",
    title: "Todas as conversas que você pode ver",
    badgeClass: "",
  },
};

const ALL_ATTENTION_FILTERS: ConversationAttentionFilter[] = [
  "all",
  "mine",
  "attention",
  "ia",
];

export const DEFAULT_INBOX_TAB_ORDER_WITH_ALL: ConversationAttentionFilter[] = [
  "all",
  "mine",
  "attention",
  "ia",
];

export const DEFAULT_INBOX_TAB_ORDER_ASSIGNED_ONLY: ConversationAttentionFilter[] = [
  "mine",
  "attention",
  "ia",
];

export type ConversationsVisibility =
  | "assigned_only"
  | "workspace_inbox"
  | "workspace_inbox_all";

/** Contexto de WMI para espelho local da ACL de integração (não substitui RLS). */
export type MemberIntegrationAccessContext = {
  isOwner: boolean;
  workspaceRestricted: boolean;
  allowedIntegrationIds: ReadonlySet<string>;
};

export interface FilterableConversation {
  is_closed?: boolean;
  state: string;
  integration_id?: string | null;
  assigned_member_id?: string | null;
  assigned_member_ids?: string[];
  escalated_by_agent_at?: string | null;
  isUrgent?: boolean;
  isUnread?: boolean;
}

function getAssignedMemberIds(conv: FilterableConversation): string[] {
  if (conv.assigned_member_ids?.length) return conv.assigned_member_ids;
  if (conv.assigned_member_id) return [conv.assigned_member_id];
  return [];
}

export function needsHandoffAttention(conv: FilterableConversation): boolean {
  return Boolean(conv.escalated_by_agent_at) || Boolean(conv.isUrgent);
}

export function isConversationOpen(conv: FilterableConversation): boolean {
  return !conv.is_closed;
}

export function isMineConversation(
  conv: FilterableConversation,
  currentMemberId: string | null | undefined
): boolean {
  return Boolean(
    currentMemberId && getAssignedMemberIds(conv).includes(currentMemberId),
  );
}

/** Mesmo critério do badge "IA ativa" no card: aberta, state active, sem escalação/urgente. */
export function isIaActiveConversation(conv: FilterableConversation): boolean {
  return (
    isConversationOpen(conv) &&
    conv.state === "active" &&
    !needsHandoffAttention(conv)
  );
}

export function isAttentionConversation(conv: FilterableConversation): boolean {
  return needsHandoffAttention(conv) && isConversationOpen(conv);
}

/**
 * Escopo de status efetivo da lista (abertas vs encerradas).
 * Aplica em todas as abas de atenção, inclusive "Todas".
 */
export function resolveStatusScope(
  _attentionFilter: ConversationAttentionFilter,
  statusFilter: ConversationStatusFilter,
): ConversationStatusFilter {
  return statusFilter;
}

export function matchesStatusFilter(
  conv: FilterableConversation,
  statusFilter: ConversationStatusFilter | null
): boolean {
  if (!statusFilter) return true;
  if (statusFilter === "closed") return Boolean(conv.is_closed);
  return isConversationOpen(conv);
}


export function matchesAttentionFilter(
  conv: FilterableConversation,
  attentionFilter: ConversationAttentionFilter,
  currentMemberId: string | null | undefined
): boolean {
  switch (attentionFilter) {
    case "mine":
      return isMineConversation(conv, currentMemberId);
    case "attention":
      return isAttentionConversation(conv);
    case "ia":
      return isIaActiveConversation(conv);
    case "all":
    default:
      return true;
  }
}

export function isConversationAccessibleByIntegration(
  conv: Pick<FilterableConversation, "integration_id">,
  ctx: MemberIntegrationAccessContext | null | undefined,
): boolean {
  if (!ctx || !ctx.workspaceRestricted) return true;
  if (ctx.isOwner) return true;
  if (ctx.allowedIntegrationIds.size === 0) return false;
  if (!conv.integration_id) return false;
  return ctx.allowedIntegrationIds.has(conv.integration_id);
}

export function isConversationVisibleToMember(
  conv: FilterableConversation,
  opts: {
    isAdmin: boolean;
    currentMemberId: string | null | undefined;
    conversationsVisibility: ConversationsVisibility;
    integrationAccess?: MemberIntegrationAccessContext | null;
  },
): boolean {
  if (!isConversationAccessibleByIntegration(conv, opts.integrationAccess)) return false;

  if (opts.isAdmin) return true;

  if (opts.conversationsVisibility === "workspace_inbox_all") return true;

  const assignedIds = getAssignedMemberIds(conv);
  if (assignedIds.length > 0) {
    return (
      opts.currentMemberId != null && assignedIds.includes(opts.currentMemberId)
    );
  }

  return opts.conversationsVisibility === "workspace_inbox";
}

export type ConversationFilterCounts = {
  mine: number;
  attention: number;
  ia: number;
  all: number;
  open: number;
  closed: number;
  unread: number;
};

export function computeConversationFilterCounts(
  conversations: FilterableConversation[],
  currentMemberId: string | null | undefined,
  opts: {
    isAdmin: boolean;
    conversationsVisibility: ConversationsVisibility;
    integrationAccess?: MemberIntegrationAccessContext | null;
    /** Quando definido, contadores de atenção (Minhas, IA ativa, etc.) só contam conversas neste status. */
    statusFilter?: ConversationStatusFilter | null;

  }
): ConversationFilterCounts {
  const visible = conversations.filter((c) =>
    isConversationVisibleToMember(c, {
      isAdmin: opts.isAdmin,
      currentMemberId,
      conversationsVisibility: opts.conversationsVisibility,
      integrationAccess: opts.integrationAccess,
    })
  );

  const attentionScope = opts.statusFilter
    ? visible.filter((c) => matchesStatusFilter(c, opts.statusFilter!))
    : visible;

  return {
    mine: attentionScope.filter((c) => isMineConversation(c, currentMemberId)).length,
    attention: attentionScope.filter((c) => isAttentionConversation(c)).length,
    ia: attentionScope.filter((c) => isIaActiveConversation(c)).length,
    all: attentionScope.length,
    open: visible.filter((c) => isConversationOpen(c)).length,
    closed: visible.filter((c) => !isConversationOpen(c)).length,
    unread: attentionScope.filter((c) => c.isUnread).length,
  };
}

export function resolveConversationsVisibility(
  raw: unknown,
  isAdmin: boolean
): ConversationsVisibility {
  if (isAdmin) return "workspace_inbox";
  if (
    raw === "assigned_only" ||
    raw === "workspace_inbox" ||
    raw === "workspace_inbox_all"
  ) {
    return raw;
  }
  return "workspace_inbox";
}

export function showAllAttentionTab(
  visibility: ConversationsVisibility,
  isAdmin: boolean
): boolean {
  return (
    isAdmin ||
    visibility === "workspace_inbox" ||
    visibility === "workspace_inbox_all"
  );
}

const STORAGE_KEY_PREFIX = "conversations-inbox-filters";

export function normalizeInboxTabOrder(
  raw: unknown,
  showAllTab: boolean
): ConversationAttentionFilter[] {
  const allowed = new Set(
    showAllTab ? ALL_ATTENTION_FILTERS : DEFAULT_INBOX_TAB_ORDER_ASSIGNED_ONLY
  );
  const fallback = showAllTab
    ? DEFAULT_INBOX_TAB_ORDER_WITH_ALL
    : DEFAULT_INBOX_TAB_ORDER_ASSIGNED_ONLY;

  if (!Array.isArray(raw)) return [...fallback];

  const seen = new Set<ConversationAttentionFilter>();
  const result: ConversationAttentionFilter[] = [];

  for (const item of raw) {
    if (
      (item === "mine" ||
        item === "attention" ||
        item === "ia" ||
        item === "all") &&
      allowed.has(item) &&
      !seen.has(item)
    ) {
      seen.add(item);
      result.push(item);
    }
  }

  for (const tab of fallback) {
    if (!seen.has(tab)) result.push(tab);
  }

  return result;
}

export function defaultAttentionFromTabOrder(
  tabOrder: ConversationAttentionFilter[]
): ConversationAttentionFilter {
  return tabOrder[0] ?? "all";
}

export function loadPersistedInboxStatus(workspaceId: string): ConversationStatusFilter | null {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}-${workspaceId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { status?: ConversationStatusFilter };
    if (parsed.status === "open" || parsed.status === "closed") return parsed.status;
    return null;
  } catch {
    return null;
  }
}

export function persistInboxStatus(workspaceId: string, status: ConversationStatusFilter): void {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}-${workspaceId}`, JSON.stringify({ status }));
  } catch {
    /* ignore */
  }
}

export function defaultStatusFilter(): ConversationStatusFilter {
  return "open";
}
